import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/client";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface HandoverArea {
  key: string;
  label: string;
  count: number;
  transferable: boolean;
  note: string;
  items?: { id: string; title: string }[];
}

export interface HandoverProject {
  id: string;
  name: string;
  short_name: string;
  client_name: string;
  status: string;
  is_coordinator: boolean;
  areas: HandoverArea[];
  total: number;
  poa_count: number;
}

export interface HandoverWorkload {
  projects: HandoverProject[];
  unassigned: HandoverArea[];
  accesses: { key: string; label: string; count: number }[];
  totals: {
    projects: number;
    transferable: number;
    poa: number;
    accesses: number;
  };
}

export interface HandoverResult {
  project_id?: string;
  project_name?: string;
  moved: Record<string, number>;
  total: number;
  poa_pending?: number;
}

// ─── Query keys ──────────────────────────────────────────────────────────────

const WORKLOAD_KEY = (userId: string) => ["employee-workload", userId] as const;

// ─── Hooks ───────────────────────────────────────────────────────────────────

export function useEmployeeWorkload(userId: string, enabled = true) {
  return useQuery({
    queryKey: WORKLOAD_KEY(userId),
    queryFn: async () => {
      const resp = await apiClient.get<{ data: HandoverWorkload }>(
        `/auth/users/${userId}/workload/`,
      );
      return resp.data.data;
    },
    enabled: !!userId && enabled,
  });
}

export function useHandover(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      to_user: string;
      project?: string;
      area?: string;
      ids?: string[];
    }) => {
      const resp = await apiClient.post<{ data: HandoverResult }>(
        `/auth/users/${userId}/handover/`,
        payload,
      );
      return resp.data.data;
    },
    onSuccess: () => {
      // The successor's own load changes too, so drop every workload cache.
      qc.invalidateQueries({ queryKey: ["employee-workload"] });
      qc.invalidateQueries({ queryKey: ["handover-queue"] });
    },
  });
}

export function useRevokeAccesses(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (keys: string[]) => {
      const resp = await apiClient.post<{
        data: { removed: Record<string, number>; total: number };
      }>(`/auth/users/${userId}/revoke-accesses/`, { keys });
      return resp.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: WORKLOAD_KEY(userId) });
      qc.invalidateQueries({ queryKey: ["handover-queue"] });
    },
  });
}

export interface HandoverQueueRow {
  id: string;
  name: string;
  role: string;
  is_active: boolean;
  planned_dismissal_date: string | null;
  deactivated_at: string | null;
  days_left: number | null;
  totals: { transferable: number; poa: number; accesses: number };
}

export function useHandoverQueue() {
  return useQuery({
    queryKey: ["handover-queue"],
    queryFn: async () => {
      const resp = await apiClient.get<{ data: HandoverQueueRow[] }>(
        "/auth/users/handover-queue/",
      );
      return resp.data.data;
    },
  });
}
